import type { Theme, SxProps } from '@mui/material/styles';

import { m } from 'framer-motion';
import { forwardRef } from 'react';
import { varAlpha } from 'minimal-shared/utils';

import Box from '@mui/material/Box';
import { styled } from '@mui/material/styles';

import { Logo } from '../logo';

import type { LogoProps } from '../logo';

// ----------------------------------------------------------------------

export type LogoSpinnerProps = React.ComponentProps<'div'> & {
  /** Overall size in px (default: 120) */
  size?: number;
  sx?: SxProps<Theme>;
  slotProps?: {
    logo?: LogoProps;
  };
};

/**
 * Scalable branded spinner -- the same animated logo zoom used on the splash
 * screen, but configurable in size so it can be used inline, in cards, etc.
 */
export const LogoSpinner = forwardRef<HTMLDivElement, LogoSpinnerProps>(
  ({ size = 120, slotProps, sx, ...other }, ref) => {
    const logoSize = Math.round(size * 0.53); // ~64/120

    return (
      <SpinnerRoot ref={ref} sx={[{ width: size, height: size }, ...(Array.isArray(sx) ? sx : [sx])]} {...other}>
        <m.span
          animate={{ scale: [1, 0.9, 0.9, 1, 1], opacity: [1, 0.48, 0.48, 1, 1] }}
          transition={{ duration: 2, repeatDelay: 1, repeat: Infinity, ease: 'easeInOut' }}
        >
          <Logo
            disabled
            {...slotProps?.logo}
            sx={[
              { width: logoSize, height: logoSize },
              ...(Array.isArray(slotProps?.logo?.sx) ? (slotProps?.logo?.sx ?? []) : [slotProps?.logo?.sx]),
            ]}
          />
        </m.span>

        <PrimaryOutline
          animate={{
            scale: [1.6, 1, 1, 1.6, 1.6],
            rotate: [270, 0, 0, 270, 270],
            opacity: [0.25, 1, 1, 1, 0.25],
            borderRadius: ['25%', '25%', '50%', '50%', '25%'],
          }}
          transition={{ ease: 'linear', duration: 3.2, repeat: Infinity }}
        />

        <SecondaryOutline
          animate={{
            scale: [1, 1.2, 1.2, 1, 1],
            rotate: [0, 270, 270, 0, 0],
            opacity: [1, 0.25, 0.25, 0.25, 1],
            borderRadius: ['25%', '25%', '50%', '50%', '25%'],
          }}
          transition={{ ease: 'linear', duration: 3.2, repeat: Infinity }}
        />
      </SpinnerRoot>
    );
  }
);

// ----------------------------------------------------------------------

/** Convenience wrapper: centers the spinner and fills its parent */
export function LogoSpinnerScreen({ size, sx, ...other }: LogoSpinnerProps) {
  return (
    <Box
      sx={[
        {
          display: 'flex',
          flex: '1 1 auto',
          width: '100%',
          height: '100%',
          alignItems: 'center',
          justifyContent: 'center',
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      <LogoSpinner size={size} {...other} />
    </Box>
  );
}

// ----------------------------------------------------------------------

const SpinnerRoot = styled('div')({
  alignItems: 'center',
  position: 'relative',
  display: 'inline-flex',
  justifyContent: 'center',
});

const PrimaryOutline = styled(m.span)(({ theme }) => ({
  position: 'absolute',
  width: 'calc(100% - 20px)',
  height: 'calc(100% - 20px)',
  border: `solid 3px ${varAlpha(theme.vars.palette.primary.darkChannel, 0.24)}`,
}));

const SecondaryOutline = styled(m.span)(({ theme }) => ({
  width: '100%',
  height: '100%',
  position: 'absolute',
  border: `solid 8px ${varAlpha(theme.vars.palette.primary.darkChannel, 0.24)}`,
}));
